import { useRef, useState } from "react";
import Message from "../../components/ui/Message";
import { PRINT_LOGO_ACCEPT, preparePrintLogo } from "./printLogo";

// Voucher impreso (Mi empresa): si el voucher que imprimen las apps lleva el logo de la empresa y cuál logo.
// Solo aplica al voucher; las apps lo descargan al iniciar sesión.
interface Props {
    merchant: any;
    configMerchant: (merchant: any) => Promise<string>;
    onSaved?: (settings: { printLogo: boolean; printLogoImage: string | null }) => void;
}

export const VoucherLogoSettings = ({ merchant, configMerchant, onSaved }: Props) => {
    const initial = {
        printLogo: !!merchant?.printLogo && !!merchant?.printLogoImage,
        image: (merchant?.printLogoImage as string | null) ?? null,
    };
    const [saved, setSaved] = useState(initial);
    const [printLogo, setPrintLogo] = useState(initial.printLogo);
    const [image, setImage] = useState<string | null>(initial.image);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const fileInput = useRef<HTMLInputElement>(null);

    const imageChanged = image !== saved.image;
    const hasChanges = printLogo !== saved.printLogo || imageChanged;

    const handleFile = async (file?: File) => {
        if (!file) return;
        setIsProcessing(true);
        try {
            setImage(await preparePrintLogo(file));
            setPrintLogo(true);
        } catch (e: any) {
            setErrorMessage(e?.message || 'No se pudo procesar la imagen.');
        } finally {
            setIsProcessing(false);
            if (fileInput.current) fileInput.current.value = '';
        }
    };

    const handleRemove = () => {
        setImage(null);
        setPrintLogo(false);
    };

    const handleSave = async () => {
        if (printLogo && !image) return setErrorMessage('Suba el logo de la empresa para imprimirlo en el voucher.');

        setIsSaving(true);
        const resp = await configMerchant({
            id: merchant.id,
            printLogo,
            ...(imageChanged ? { printLogoImage: image } : {}),
        });
        setIsSaving(false);

        if (resp === 'success') {
            setSaved({ printLogo, image });
            onSaved?.({ printLogo, printLogoImage: image });
            setSuccessMessage('Configuración del voucher actualizada. Las apps la toman al volver a iniciar sesión.');
        } else {
            setErrorMessage(resp);
        }
    };

    const handleReset = () => {
        setPrintLogo(saved.printLogo);
        setImage(saved.image);
    };

    return (
        <div className="card border-0 shadow-sm">
            <div className="card-body">
                <h6 className="mb-1">Voucher impreso</h6>
                <p className="text-muted small mb-3">
                    El logo se imprime en el encabezado del voucher y del tiquete de venta que imprime el POS (no en comandas de cocina). Sin logo de la empresa el voucher lleva el de Swiftmart.
                </p>

                <div className="border rounded p-3 mb-3">
                    <div className="form-check form-switch d-flex justify-content-between align-items-center ps-0 mb-0">
                        <label className="form-check-label" htmlFor="printLogoSwitch">
                            <div className="fw-medium">Imprimir el logo de la empresa</div>
                            <div className="text-muted small">Al imprimir un voucher en la app, sale el logo de la empresa en el encabezado.</div>
                        </label>
                        <input
                            id="printLogoSwitch"
                            className="form-check-input ms-3 flex-shrink-0"
                            type="checkbox"
                            role="switch"
                            style={styles.switch}
                            checked={printLogo}
                            disabled={!image}
                            onChange={(e) => setPrintLogo(e.target.checked)}
                        />
                    </div>
                    {!image && <div className="text-muted small mt-2"><i className="bi bi-info-circle me-1" />Suba un logo para activar esta opción.</div>}
                </div>

                <div className="border rounded p-3 mb-3">
                    <div className="row g-3 align-items-center">
                        <div className="col-12 col-md-6">
                            <div className="fw-medium mb-1">Logo para imprimir</div>
                            <div className="text-muted small mb-3">
                                PNG, JPG o WEBP. Se ajusta al ancho del papel y se convierte a escala de grises, como sale en la impresora térmica.
                                Use un logo con fondo blanco o transparente y buen contraste.
                            </div>
                            <input
                                ref={fileInput}
                                id="printLogoFile"
                                type="file"
                                accept={PRINT_LOGO_ACCEPT}
                                className="d-none"
                                onChange={(e) => handleFile(e.target.files?.[0])}
                            />
                            <div className="d-flex flex-wrap gap-2">
                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => fileInput.current?.click()} disabled={isProcessing || isSaving}>
                                    {isProcessing
                                        ? <><span className="spinner-border spinner-border-sm me-1" /> Procesando...</>
                                        : <><i className="bi bi-upload me-1" />{image ? 'Cambiar logo' : 'Subir logo'}</>}
                                </button>
                                {image && (
                                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleRemove} disabled={isProcessing || isSaving}>
                                        <i className="bi bi-trash me-1" />Quitar
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Vista previa del encabezado del voucher */}
                        <div className="col-12 col-md-6 d-flex justify-content-center">
                            <div style={styles.paper} aria-label="Vista previa del voucher">
                                {printLogo && image
                                    ? <img src={image} alt="Logo de la empresa en el voucher" style={styles.logo} />
                                    : <div className="text-muted small text-center py-3" style={styles.placeholder}>Logo de Swiftmart</div>}
                                <div style={styles.paperLine} />
                                <div style={styles.paperText}>{merchant?.name}</div>
                                <div style={{ ...styles.paperLine, width: '60%' }} />
                                <div style={{ ...styles.paperLine, width: '80%' }} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-end align-items-center gap-2 pt-1">
                    {hasChanges && <span className="text-muted small me-auto"><i className="bi bi-info-circle me-1" />Tiene cambios sin guardar.</span>}
                    <button type="button" className="btn btn-outline-secondary" onClick={handleReset} disabled={!hasChanges || isSaving}>
                        Descartar
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!hasChanges || isSaving || isProcessing}>
                        {isSaving
                            ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</>
                            : <><i className="bi bi-check-lg me-1" /> Guardar cambios</>}
                    </button>
                </div>
            </div>

            {errorMessage &&
                <Message type='error' message={errorMessage} closeDialog={() => setErrorMessage('')} handleOk={() => setErrorMessage('')} backgroundColor='rgba(0,0,0,0.4)' />}
            {successMessage &&
                <Message type='success' message={successMessage} title='Exitoso' closeDialog={() => setSuccessMessage('')} handleOk={() => setSuccessMessage('')} backgroundColor='rgba(0,0,0,0.4)' />}
        </div>
    );
};

const styles = {
    switch: {
        width: '2.75em',
        height: '1.4em',
        cursor: 'pointer',
    },
    paper: {
        width: 200,
        background: '#fff',
        border: '1px solid #dee2e6',
        borderRadius: 4,
        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        padding: '12px 10px 16px',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        gap: 6,
    },
    logo: {
        maxWidth: '100%',
        maxHeight: 84,
        objectFit: 'contain' as const,
    },
    placeholder: {
        width: '100%',
        border: '1px dashed #ced4da',
        borderRadius: 4,
    },
    paperText: {
        fontFamily: 'monospace',
        fontSize: 11,
        color: '#212529',
        textAlign: 'center' as const,
    },
    paperLine: {
        width: '100%',
        height: 6,
        borderRadius: 3,
        background: '#e9ecef',
    },
};
